import * as React from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { AppConstantsContext, TranscriptionDialogContext } from '../../../../context';
import { JobType } from '../../../../entities';

import { HELPER_TEXT, STEP_TITLE, STEPS } from './TranscriptionDialog.config';

import type { TranscriptionDialogContextValue } from '../../../../context/TranscriptionDialogContext/TranscriptionDialogContext';
import TranscriptionDialogWrapper from './TranscriptionDialogWrapper';
import { useWizard } from './Wizard/WizardContext';
import TranscriptionCloseDialog from './TranscriptionCloseDialog';
import { useDurationTracking } from './DurationTracking.hook';
import {
  TrackingCategory,
  trackingService,
  TrackingTranscriptionParams,
} from '../../../../services';

export const StepFrame: React.FC<{
  submitting: boolean;
  showCloseDialog: boolean;
  setShowCloseDialog: (show: boolean) => void;
  handleClose: (step: string) => void;
  children: React.ReactNode;
  id: keyof typeof STEPS;
  backClickSettings?: Partial<TranscriptionDialogContextValue['settings']>;
}> = ({
  submitting,
  handleClose,
  children,
  id,
  backClickSettings,
  showCloseDialog,
  setShowCloseDialog,
}) => {
  const { cursor, prev, current } = useWizard();
  const { setSettings, settings } = useContext(TranscriptionDialogContext);
  const { jobType } = useContext(AppConstantsContext);
  const { t } = useTranslation();
  const { getElapsedSeconds } = useDurationTracking(true);

  React.useEffect(() => {
    setShowCloseDialog(showCloseDialog);
  }, [showCloseDialog]);

  React.useEffect(() => {
    if (getElapsedSeconds() > 0.2) {
      const trackingValues = {
        currentStep: current?.id,
        duration: getElapsedSeconds(),
      };
      trackingService.valueObjectEvent(
        TrackingCategory.Transcription,
        TrackingTranscriptionParams.action.stepChange,
        { ...trackingValues }
      );
    }
  }, [current, getElapsedSeconds]);

  const titleFor = (step: keyof typeof STEPS) => {
    if (id === STEPS.CHOOSE_INSTRUMENT_MODEL && jobType === JobType.TranscribeStudio) {
      return t('home.input.dialog.choose_instrument.title_multi');
    }
    const key = STEP_TITLE[step];
    return key ? t(key) : '';
  };

  const helpFor = (step: keyof typeof STEPS) => {
    if (id === STEPS.CHOOSE_INSTRUMENT_MODEL && jobType === JobType.TranscribeStudio) {
      return t('home.input.dialog.choose_instrument.help_multi');
    }
    const key = HELPER_TEXT[step];
    return key ? t(key) : '';
  };
  const onClickBack = () => {
    setSettings({ ...settings, ...backClickSettings });
    prev();
  };

  const handleCloseDialog = () => {
    handleClose(current?.id);
  };
  const handleCloseConfirm = () => {
    setShowCloseDialog(true);
  };
  return (
    <>
      <TranscriptionDialogWrapper
        submitting={submitting}
        handleClose={handleCloseConfirm}
        handleBackClick={onClickBack}
        currentStep={Math.max(cursor, 0)}
        title={titleFor(id as keyof typeof STEPS)}
        helperText={helpFor(id as keyof typeof STEPS)}>
        {children}
      </TranscriptionDialogWrapper>

      <TranscriptionCloseDialog
        open={showCloseDialog}
        setOpen={setShowCloseDialog}
        handleClose={handleCloseDialog}
        currentStep={current?.id}
      />
    </>
  );
};
